/**
 * Whether a media query matches, kept in step with the viewport.
 *
 * Subscribed rather than measured in an effect: an effect paints the wide form
 * first and corrects it, and `react-hooks/set-state-in-effect` bans the
 * correction anyway.
 *
 * @module components/ui/useMediaQuery
 */

import { useCallback, useSyncExternalStore } from "react";

/** Below Tailwind's `lg`, where the sidebar collapses and one column fits. */
export const BELOW_LG = "(max-width: 63.999rem)";

/**
 * @example
 * ```ts
 * const narrow = useMediaQuery("(max-width: 39.999rem)");
 * ```
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = typeof window.matchMedia === "function" ? window.matchMedia(query) : null;
      list?.addEventListener("change", onChange);
      return () => list?.removeEventListener("change", onChange);
    },
    [query],
  );
  const read = useCallback(
    // jsdom ships no matchMedia and no layout either, so a component under
    // test sees the wide form unless the test says otherwise.
    () => typeof window.matchMedia === "function" && window.matchMedia(query).matches,
    [query],
  );
  // Server-rendered pages have no viewport to measure, so they take the wide
  // form too. Nothing renders this app on a server today.
  return useSyncExternalStore(subscribe, read, () => false);
}
